import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translateText } from '../utils/translate';
export type Locale = 'fr' | 'en';
interface LanguageContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}
const LanguageContext = createContext<LanguageContextValue>({
  locale: 'fr',
  setLocale: () => {}
});
const ATTRIBUTES = ['placeholder', 'aria-label', 'title', 'alt'] as const;

/** Textes d'origine, pour rétablir le français sans recharger la page. */
const originalText = new WeakMap<Text, string>();
/** Dernière valeur écrite par le traducteur, pour détecter les mises à jour de React. */
const appliedText = new WeakMap<Text, string>();
const originalAttribute = new WeakMap<Element, Record<string, string>>();
function collectTextNodes(root: Node): Text[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (parent.closest('script, style, [data-no-translate]')) return NodeFilter.FILTER_REJECT;
      return node.nodeValue && node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    }
  });
  const nodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    nodes.push(current as Text);
    current = walker.nextNode();
  }
  return nodes;
}
function apply(locale: Locale) {
  const root = document.body;
  if (!root) return;
  collectTextNodes(root).forEach(node => {
    const current = node.nodeValue ?? '';
    // React a réécrit ce nœud : le texte courant devient la nouvelle source.
    if (!originalText.has(node) || appliedText.get(node) !== current) {
      originalText.set(node, current);
    }
    const source = originalText.get(node) ?? '';
    const next = locale === 'en' ? translateText(source) : source;
    if (current !== next) node.nodeValue = next;
    appliedText.set(node, next);
  });
  root.querySelectorAll<HTMLElement>('[placeholder], [aria-label], [title], [alt]').forEach(element => {
    if (element.closest('[data-no-translate]')) return;
    const saved = originalAttribute.get(element) ?? {};
    ATTRIBUTES.forEach(attribute => {
      const current = element.getAttribute(attribute);
      if (current === null) return;
      if (!(attribute in saved)) saved[attribute] = current;
      const source = saved[attribute];
      const next = locale === 'en' ? translateText(source) : source;
      if (current !== next) element.setAttribute(attribute, next);
    });
    originalAttribute.set(element, saved);
  });
}
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [locale, setLocale] = useState<Locale>('fr');
  useEffect(() => {
    document.documentElement.lang = locale;
    let frame = 0;
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        observer.disconnect();
        apply(locale);
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          characterData: true
        });
      });
    };
    const observer = new MutationObserver(schedule);
    schedule();
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [locale]);
  const value = useMemo(() => ({
    locale,
    setLocale
  }), [locale]);
  return <LanguageContext.Provider value={value} data-id="element-2873" data-mp="3m7ox9rd2vguf">{children}</LanguageContext.Provider>;
}
export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}

/** Bascule FR ↔ EN. */
export function useToggleLocale() {
  const {
    locale,
    setLocale
  } = useLanguage();
  return useCallback(() => setLocale(locale === 'fr' ? 'en' : 'fr'), [locale, setLocale]);
}